import type { QueryClient } from "@tanstack/react-query";
import { apiClient, ApiError } from "@/lib/apiClient";
import { clearClientAuthState } from "./clearClientAuthState";
import { queryKeys } from "@/lib/query/queryKeys";
import { suppressGlobalErrorToastMeta } from "@/lib/query/queryMeta";
import type { CurrentUser } from "@/types/api/user";

export const currentUserQueryOptions = {
  meta: suppressGlobalErrorToastMeta,
  queryKey: queryKeys.users.me(),
  queryFn: () => apiClient.getData<CurrentUser>("/users/me"),
  retry: (failureCount: number, error: unknown) =>
    !(error instanceof ApiError && error.statusCode === 401) && failureCount < 1,
  staleTime: 5 * 60 * 1000,
} as const;

export async function fetchCurrentUser(
  queryClient: QueryClient,
  options?: { fresh?: boolean },
): Promise<CurrentUser | null> {
  try {
    // fresh → staleTime 0; same query key dedupes parallel guard calls
    return await queryClient.fetchQuery(
      options?.fresh
        ? { ...currentUserQueryOptions, staleTime: 0 }
        : currentUserQueryOptions,
    );
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 401) {
      queryClient.setQueryData(queryKeys.users.me(), null);
      return null;
    }
    throw error;
  }
}

/** POST /auth/sign-out + clear local storage. */
export async function endServerSession(): Promise<void> {
  try {
    await apiClient.post<void>("/auth/sign-out", {});
  } catch {
    // continue cleanup even if the API call fails
  }

  await clearClientAuthState();
}
